import { z } from 'zod';
import { membershipActionEnum, membershipStatusEnum, CAPABILITIES, STAFF_LEVEL_KEYS } from '../enums';
import { countryCodeSchema, emailSchema, isoDateTimeSchema, nameSchema, paginationQuerySchema, phoneSchema, urlSchema } from './common';

export const capabilitiesSchema = z.array(z.enum(CAPABILITIES)).max(CAPABILITIES.length);
// partialRecord: enum-key records are exhaustive in Zod 4; staff levels are sparse
export const staffLevelsSchema = z.partialRecord(z.enum(STAFF_LEVEL_KEYS), z.number().int().min(0).max(999));

export const openingHoursSchema = z.partialRecord(
  z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']),
  z.object({ open: z.string().regex(/^\d{2}:\d{2}$/), close: z.string().regex(/^\d{2}:\d{2}$/) }).nullable(),
);

export const inviteGarageSchema = z.object({
  name: z.string().trim().min(2).max(150),
  countryCode: countryCodeSchema,
  ownerFirstName: nameSchema,
  ownerLastName: nameSchema,
  ownerEmail: emailSchema,
  addressLine1: z.string().trim().max(200).optional(),
  city: z.string().trim().max(100).optional(),
  postcode: z.string().trim().max(20).optional(),
  lkqAccountNumber: z.string().trim().max(50).optional(),
});
export type InviteGaragePayload = z.infer<typeof inviteGarageSchema>;

export const updateGarageSchema = z.object({
  name: z.string().trim().min(2).max(150),
  legalName: z.string().trim().max(200).nullable(),
  companyNumber: z.string().trim().max(20).nullable(),
  vatNumber: z.string().trim().max(20).nullable(),
  email: emailSchema.nullable(),
  phone: phoneSchema.nullable(),
  website: urlSchema.nullable(),
}).partial();
export type UpdateGaragePayload = z.infer<typeof updateGarageSchema>;

// One endpoint per docs/05 W6 — action + required reason; every change writes
// membership_events in the same transaction.
export const membershipTransitionSchema = z.object({
  action: membershipActionEnum,
  reason: z.string().trim().min(3).max(500),
  membershipEndsAt: isoDateTimeSchema.nullable().optional(),
});
export type MembershipTransitionPayload = z.infer<typeof membershipTransitionSchema>;

export const archiveGarageSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

export const garageListQuerySchema = paginationQuerySchema.extend({
  status: z.string().optional(),
  countryCode: countryCodeSchema.optional(),
  search: z.string().trim().max(100).optional(),
  includeArchived: z.coerce.boolean().default(false),
});
export type GarageListQuery = z.infer<typeof garageListQuerySchema>;

// Lightweight point for the concept dashboard's garage coverage map: every
// non-archived garage with a plottable primary-location coordinate. Coordinates
// are numbers — the service coerces the DECIMAL columns (pg returns them as
// strings). Status is the membership status, encoded as marker colour.
export const garageMapPointSchema = z.object({
  id: z.string(),
  name: z.string(),
  lat: z.number(),
  lng: z.number(),
  status: membershipStatusEnum,
});
export type GarageMapPoint = z.infer<typeof garageMapPointSchema>;

// Onboarding wizard (docs/01 J3): business → primary location → capabilities.
export const onboardingSchema = z.object({
  business: updateGarageSchema,
  location: z.object({
    label: z.string().trim().min(1).max(100).default('Main site'),
    addressLine1: z.string().trim().min(1).max(200),
    addressLine2: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().min(1).max(100),
    postcode: z.string().trim().min(2).max(20),
    phone: phoneSchema.nullable().optional(),
    email: emailSchema.nullable().optional(),
    openingHours: openingHoursSchema.nullable().optional(),
    lkqAccountNumber: z.string().trim().max(50).nullable().optional(),
  }),
  capabilities: capabilitiesSchema,
  staffLevels: staffLevelsSchema,
});
export type OnboardingPayload = z.infer<typeof onboardingSchema>;
